/** Pure delegation routing for Control UI chat (JAISIU-73). No gateway calls. */

import { normalizeAgentId } from "../../../../src/routing/session-key.js";
import { parseAgentSessionKey } from "../../../../src/sessions/session-key-utils.js";

export type AgentRef = { id: string; name?: string };

export type ParsedDelegation =
  | { kind: "none"; message: string }
  | { kind: "spawn"; agentId: string; agentName?: string; task: string }
  | { kind: "all"; task: string }
  | { kind: "active"; task: string }
  | { kind: "best-of-n"; n: number; task: string };

/**
 * Manual delegation parity with TUI: `@agentId remainder` or `@agentName remainder`.
 */
export function parseMessageForSubAgent(text: string, agents: AgentRef[]): ParsedDelegation {
  const trimmed = text.trim();

  const bestOfMatch = trimmed.match(/^\/best-of-n\s+(\d+)\s+(.+)$/is);
  if (bestOfMatch) {
    const n = Math.max(1, Math.min(10, parseInt(bestOfMatch[1], 10)));
    const task = bestOfMatch[2].trim();
    if (task.length > 0) {
      return { kind: "best-of-n", n, task };
    }
  }

  const atMatch = trimmed.match(/^@(\S+)\s+(.+)$/s);
  if (atMatch) {
    const agentRef = atMatch[1].toLowerCase();
    const task = atMatch[2].trim();
    if (agentRef === "all" || agentRef === "everyone") {
      return { kind: "all", task };
    }
    if (agentRef === "active") {
      return { kind: "active", task };
    }
    const agent = agents.find(
      (a) => a.id.toLowerCase() === agentRef || a.name?.toLowerCase() === agentRef,
    );
    if (!agent) {
      return { kind: "none", message: text };
    }
    return { kind: "spawn", agentId: agent.id, agentName: agent.name, task };
  }

  return { kind: "none", message: text };
}

/**
 * When already on an agent's main session (e.g. agent:main:main), `@main …` should use
 * chat.send — not sessions_spawn (which opens a parallel subagent worker).
 */
export function shouldSendToMainSessionInsteadOfSpawn(
  sessionKey: string,
  targetAgentId: string,
): boolean {
  const parsed = parseAgentSessionKey(sessionKey.trim());
  if (!parsed || parsed.rest !== "main") {
    return false;
  }
  return normalizeAgentId(targetAgentId) === normalizeAgentId(parsed.agentId);
}

export type GatewayModelChoice = {
  id: string;
  name: string;
  provider: string;
};

/** Prefer a small Ollama model when gateway lists one (trivial / low-stakes routes). */
export function pickOllamaModel(
  models: GatewayModelChoice[],
  preferredIds?: string[],
): string | undefined {
  const ollama = models.filter((m) => m.provider?.toLowerCase() === "ollama");
  if (ollama.length === 0) {
    return undefined;
  }
  if (preferredIds?.length) {
    const lower = new Set(preferredIds.map((id) => id.toLowerCase()));
    const hit = ollama.find(
      (m) => lower.has(`${m.provider}/${m.id}`.toLowerCase()) || lower.has(m.id.toLowerCase()),
    );
    if (hit) {
      return `${hit.provider}/${hit.id}`;
    }
  }
  const first = ollama[0];
  return `${first.provider}/${first.id}`;
}

export type AutoDelegation =
  | { action: "main" }
  | { action: "spawn"; task: string; model?: string; reason?: string };

/**
 * Plain Fleet chat messages go to the active session via chat.send.
 * Subagents are only spawned for explicit @-mentions, /best-of-n, or orchestrate options.
 */
export function classifyAutoDelegation(_text: string): AutoDelegation {
  return { action: "main" };
}

export function shouldRouteTrivialToOllama(auto: AutoDelegation): boolean {
  return auto.action === "spawn" && auto.reason === "trivial Q&A route";
}

export function getActiveAgentIds(
  chatSubagents: Array<{ agentId: string; status: string }>,
): string[] {
  const byAgent = new Map<string, boolean>();
  for (const c of chatSubagents) {
    const isActive = c.status === "running" || c.status === "queued" || c.status === "spawning";
    const current = byAgent.get(c.agentId);
    if (current === undefined || !isActive) {
      byAgent.set(c.agentId, isActive);
    }
  }
  const result: string[] = [];
  for (const [id, active] of byAgent) {
    if (active) result.push(id);
  }
  return result;
}

const AGENT_HINTS: Array<{ pattern: RegExp; tags: string[] }> = [
  {
    pattern: /\b(code|implement|fix|refactor|test|debug|typescript|javascript|ts|js)\b/i,
    tags: ["coder", "dev", "engineer", "backend", "frontend", "qa"],
  },
  {
    pattern: /\b(research|investigate|analyze|compare|summary|summarize|docs|documentation)\b/i,
    tags: ["research", "analyst", "doc", "writer"],
  },
  {
    pattern: /\b(infra|deploy|docker|kubernetes|k8s|ci|cd|pipeline|ops|sre)\b/i,
    tags: ["infra", "ops", "sre", "platform", "devops"],
  },
  {
    pattern: /\b(security|auth|permission|vulnerability|audit|token|secret)\b/i,
    tags: ["security", "sec", "auth"],
  },
];

function scoreAgentForTask(agent: AgentRef, task: string): number {
  const hay = `${agent.id} ${agent.name ?? ""}`.toLowerCase();
  let score = 0;
  for (const hint of AGENT_HINTS) {
    if (!hint.pattern.test(task)) {
      continue;
    }
    for (const tag of hint.tags) {
      if (hay.includes(tag)) {
        score += 3;
      }
    }
  }
  if (agent.id.toLowerCase() === "main") {
    score -= 2;
  }
  return score;
}

export function pickBestFitAgentIds(params: {
  agents: AgentRef[];
  task: string;
  max: number;
}): string[] {
  const cap = Math.max(1, Math.min(params.max, 8));
  const ranked = [...params.agents]
    .map((a) => ({ id: a.id, score: scoreAgentForTask(a, params.task) }))
    .sort((a, b) => b.score - a.score || a.id.localeCompare(b.id));
  const nonMain = ranked.filter((r) => r.id.toLowerCase() !== "main");
  const source = nonMain.length > 0 ? nonMain : ranked;
  return source.slice(0, cap).map((r) => r.id);
}
